import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import store, { hmdaActions } from '@apply/adminStore';
import './hmda.css';
import moment from 'moment-timezone';
import DataGrid, { Paging, Pager, Selection, Toolbar, Item, Scrolling } from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import DateBox from 'devextreme-react/date-box';
import { HmdaApi } from '@apply/api';
import { ConfirmModal } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { InformModal, StateSelect, CountySelect, ZipCodeInput, BfiNumber } from '../../common/SharedComponents';
import { formatCurrency, formatZip, handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Tooltip } from 'devextreme-react/tooltip';
import { FieldType, HMDALoanTypeOptions } from '../common/HmdaConstants';
import { RefreshEvents } from '../../common/SharedEvents';
import { TextBox } from 'devextreme-react/text-box';
import { TagBox } from 'devextreme-react/tag-box';
import { FeatureType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';

export const HmdaSummary = ({ hmdaStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const { dispatch } = store;
    const hmdaApi = new HmdaApi.HmdaApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const dataGrid = useRef(null);
    const { user } = store.getState();

    const [fields, set_fields] = useState([]);
    const [bfiRelevantVisible, set_bfiRelevantVisible] = useState(true);
    const [rowsWithAlertsVisible, set_RowsWithAlertsVisible] = useState(false);
    const [nonExportableVisible, set_NonExportableVisible] = useState(false);
    const [bfiNumber, set_bfiNumber] = useState('');
    const [applicationTypes, set_applicationTypes] = useState([]);
    const [applyStartDate, set_applyStartDate] = useState(null);
    const [applyEndDate, set_applyEndDate] = useState(null);
    const [actionTakenStartDate, set_actionTakenStartDate] = useState(null);
    const [actionTakenEndDate, set_actionTakenEndDate] = useState(null);
    const [uli, set_uli] = useState('');
    const [street, set_street] = useState('');
    const [city, set_city] = useState('');
    const [statecd, set_statecd] = useState('');
    const [zip, set_zip] = useState('');
    const [county, set_county] = useState('');
    const [openConfirmDelete, set_openConfirmDelete] = useState(false);
    const [dataStore, set_dataStore] = useState(hmdaStore?.store);
    const [informModalVisible, set_informModalVisible] = useState(false);
    const [openConfirmRequeueApp, set_openConfirmRequeueApp] = useState(false);
    const [openConfirmRequeueLoan, set_openConfirmRequeueLoan] = useState(false);

    const bfiNumberRef = useRef(bfiNumber);
    const applicationTypesRef = useRef(applicationTypes);
    const applyStartDateRef = useRef(applyStartDate);
    const applyEndDateRef = useRef(applyEndDate);
    const actionTakenStartDateRef = useRef(actionTakenStartDate);
    const actionTakenEndDateRef = useRef(actionTakenEndDate);
    const uliRef = useRef(uli);
    const streetRef = useRef(street);
    const cityRef = useRef(city);
    const stateRef = useRef(statecd);
    const zipRef = useRef(zip);
    const countyRef = useRef(county);
    const rowsWithAlertsVisibleRef = useRef(rowsWithAlertsVisible);
    const nonExportableVisibleRef = useRef(nonExportableVisible);

    const allowedPageSizes = [15, 25, 50, 100];
    const defaultPageSize = allowedPageSizes[1];

    const hmdaStyles = {
        formColumn: { paddingBottom: '3px' },
    };

    useEffect(() => {
        //Load up Fields and retrieve Data
        async function load() {
            await loadFields();

            refreshEvents.addRefreshListener(_afterRefresh);
            return () => {
                refreshEvents.removeRefreshListener(_afterRefresh);
            };
        }

        load();
    }, []);

    useEffect(() => {
        if (hmdaStore) {
            hmdaStore.setOnLoad('id', dataFunction, searchState?.searchText?.hmda);
            set_dataStore(hmdaStore.store);
        }
    }, [hmdaStore]);

    useEffect(() => {
        if (
            !bfiNumber &&
            !applicationTypes &&
            (!applyStartDate || !applyEndDate) &&
            (!actionTakenStartDate || !actionTakenEndDate) &&
            !uli &&
            !street &&
            !city &&
            !statecd &&
            !county &&
            !zip &&
            !dataGrid?.current
        ) {
            return;
        }

        bfiNumberRef.current = bfiNumber;
        applicationTypesRef.current = applicationTypes;
        applyStartDateRef.current = applyStartDate;
        applyEndDateRef.current = applyEndDate;
        actionTakenStartDateRef.current = actionTakenStartDate;
        actionTakenEndDateRef.current = actionTakenEndDate;
        uliRef.current = uli;
        streetRef.current = street;
        cityRef.current = city;
        stateRef.current = statecd;
        countyRef.current = county;
        zipRef.current = zip;
        rowsWithAlertsVisibleRef.current = rowsWithAlertsVisible;
        nonExportableVisibleRef.current = nonExportableVisible;

        _afterRefresh();
    }, [
        applyStartDate,
        applyEndDate,
        bfiNumber,
        applicationTypes,
        actionTakenStartDate,
        actionTakenEndDate,
        uli,
        street,
        city,
        statecd,
        county,
        zip,
        rowsWithAlertsVisible,
        nonExportableVisible,
    ]);

    const _afterRefresh = () => {
        const grid = dataGrid?.current?.instance;
        if (grid !== null && grid !== undefined) {
            grid.refresh();
        }
    };

    const dataFunction = (criteria) => {
        criteria.bfiNumber = bfiNumberRef.current;
        criteria.types = applicationTypesRef.current;
        criteria.applyStartDate = applyStartDateRef.current
            ? new Date(applyStartDateRef.current).toLocaleDateString('en-us')
            : null;
        criteria.applyEndDate = applyEndDateRef.current ? new Date(applyEndDateRef.current).toLocaleDateString('en-us') : null;
        criteria.actionTakenStartDate = actionTakenStartDateRef.current
            ? new Date(actionTakenStartDateRef.current).toLocaleDateString('en-us')
            : null;
        criteria.actionTakenEndDate = actionTakenEndDateRef.current
            ? new Date(actionTakenEndDateRef.current).toLocaleDateString('en-us')
            : null;
        criteria.uli = uliRef.current;
        criteria.street = streetRef.current;
        criteria.city = cityRef.current;
        criteria.state = stateRef.current;
        criteria.county = countyRef.current;
        criteria.zip = zipRef.current;
        criteria.showOnlyRowsWithAlerts = rowsWithAlertsVisibleRef.current;
        criteria.showNonExportableRows = nonExportableVisibleRef.current;

        return hmdaApi.getData(criteria);
    };

    const setHorizontalScroll = () => {
        const element = document.querySelector('.dx-scrollable-container');
        if (element) {
            element.addEventListener('wheel', (event) => {
                event.preventDefault();
                element.scrollBy({
                    left: event.deltaY < 0 ? -60 : 60,
                });
            });
        }
    };

    const loadFields = async () => {
        const response = await hmdaApi.getFields();
        if (response.statusCode !== 200) {
            return;
        }
        set_fields(response.result);
    };

    const customizeColumns = (cols) => {
        if (!fields || fields.length === 0) {
            return true;
        }

        let index = 0;
        cols.forEach((c) => {
            if (c.dataField === 'id' || c.dataField === 'applicationId') {
                c.visible = false;
            } else if (c.dataField === 'bfiNumber') {
                c.visible = true;
                c.cellTemplate = 'alphaNumTemplate';
                c.width = 100;
                c.cssClass = 'non-editable';
                c.caption = 'BFI #';
                c.visible = true;
                c.allowExporting = false;
            } else {
                index += 1;
                const field = fields[index - 1];

                c.caption = index + ': ' + field.shortName;
                c.visible = !bfiRelevantVisible || (bfiRelevantVisible && field.bfiRelevant);
                if (!field.isEditable) {
                    c.cssClass = 'non-editable';
                }

                if (!field.IsExportable) {
                    c.allowExporting = false;
                }
                switch (field.fieldTypeId) {
                    case FieldType.AlphaNumeric:
                        c.width = 200;
                        c.cellTemplate = 'alphaNumTemplate';
                        break;
                    case FieldType.Currency:
                        c.width = 150;
                        c.cellTemplate = 'currencyTemplate';
                        break;
                    case FieldType.Integer:
                        c.width = 150;
                        c.cellTemplate = 'integerTemplate';
                        break;
                    case FieldType.Date:
                        c.width = 150;
                        c.cellTemplate = 'dateTemplate';
                        break;
                    case FieldType.Lookup:
                        c.width = 250;
                        c.cellTemplate = 'lookupTemplate';
                        break;
                    case FieldType.Decimal:
                        c.width = 150;
                        c.cellTemplate = 'decimalTemplate';
                        break;
                    case FieldType.DecimalSinglePrecision:
                        c.width = 150;
                        c.cellTemplate = 'decimalSinglePrecisionTemplate';
                        break;
                    case FieldType.ZipCode:
                        c.width = 200;
                        c.cellTemplate = 'zipCodeTemplate';
                        break;
                }
            }
        });
    };

    const renderOptions = (field, e) => {
        let option = null;
        if (field?.options && field?.options.length > 0) {
            option = field.options.find((o) => o.code === e.value);
        }
        if (option) {
            const targetId = `${e.column.dataField}_${e.data.id}}`;
            const data = `${option.code} - ${option.value}`;
            if (data.length > 35) {
                return (
                    <>
                        <div
                            data-tip
                            data-for={targetId}
                        >
                            {data}
                        </div>
                        <Tooltip
                            target={`[data-for="${targetId}"]`}
                            showEvent='mouseenter'
                            hideEvent='mouseleave'
                            hideOnOutsideClick={false}
                        >
                            {data}
                        </Tooltip>
                    </>
                );
            } else {
                return data;
            }
        }
        return null;
    };

    const lookupRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        return renderOptions(field, e);
    };

    const dateRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);

        if (field.outputFormat != null) {
            return moment.utc(e.value).format(field.outputFormat);
        } else {
            return e.value && new Date(e.value).toLocaleDateString('en-us');
        }
    };

    const alphaNumRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        var retVal = renderOptions(field, e);
        if (retVal) {
            return retVal;
        }
        if (e.value) {
            return e.value;
        }
    };

    const decimalRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        var retVal = renderOptions(field, e);
        if (retVal) {
            return retVal;
        }
        return e.value && parseFloat(e.value).toFixed(2);
    };

    const decimalSinglePrecisionRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        var retVal = renderOptions(field, e);
        if (retVal) {
            return retVal;
        }
        return e.value && parseFloat(e.value).toFixed(1);
    };

    const integerRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        var retVal = renderOptions(field, e);
        if (retVal) {
            return retVal;
        }
        return e.value;
    };

    const currencyRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        var retVal = renderOptions(field, e);
        if (retVal) {
            return retVal;
        }

        if (field.outputFormat != null && field.outputFormat === 'NoFormat') {
            //Drop Decimals
            return e.value && Number(e.value).toFixed();
        } else {
            return e.value && formatCurrency(e.value, 0);
        }
    };

    const zipCodeRender = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);
        var retVal = renderOptions(field, e);
        if (retVal) {
            return retVal && formatZip(retVal);
        }
        if (e.value) {
            return e.value && formatZip(e.value);
        }
    };

    const onContentReady = (e) => {
        setHorizontalScroll();
    };

    const onCellDblClick = (e) => {
        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        if (!isNaN(fieldId)) {
            dispatch(
                hmdaActions.setHmdaEditModal({
                    hmdaEditModal: {
                        isOpen: true,
                        fieldId,
                        fields: [...fields],
                        data: { ...e.data },
                    },
                })
            );
        }
    };

    const onCellPrepared = (e) => {
        if (e.rowType !== 'data' || !fields || !e.column?.dataField) {
            return;
        }

        const fieldId = parseInt(e.column.dataField.replace('field', ''));
        const field = fields.find((a) => a.id === fieldId);

        if (!field) {
            return;
        }

        if (e.value) {
            fieldLevelWarnings(e, fieldId);
            return;
        }

        if (field.isRequired) {
            e.cellElement.className = 'missing';
        } else if (field.requiredByFieldId && field.requiredByFieldValue) {
            if (e.data['field' + field.requiredByFieldId] === field.requiredByFieldValue) {
                e.cellElement.className = 'missing';
                e.cellElement.innerText = 'MISSING!';
            }
        }
    };

    const fieldLevelWarnings = (e, fieldId) => {
        switch (fieldId) {
            case 81:
                if (parseInt(e.value) < 20 || parseInt(e.value) > 100) {
                    e.cellElement.className = 'warning';
                }
                break;
            case 17:
                if (!/^[0-9]{5,5}$/.test(e.value)) {
                    e.cellElement.className = 'warning';
                }
                break;
            case 18:
                if (!/^[0-9]{11,11}$/.test(e.value)) {
                    e.cellElement.className = 'warning';
                }
                break;
        }
    };

    const onDeleteClick = () => {
        const keys = dataGrid.current?.instance?.getSelectedRowKeys() ?? [];
        if (keys.length === 0) {
            set_informModalVisible(true);
            return;
        }
        set_openConfirmDelete(true);
    };

    const onFilterResetClick = () => {
        set_bfiRelevantVisible(true);
        set_RowsWithAlertsVisible(false);
        set_bfiNumber('');
        set_applicationTypes([]);
        set_applyStartDate(null);
        set_applyEndDate(null);
        set_actionTakenStartDate(null);
        set_actionTakenEndDate(null);
        set_uli('');
        set_street('');
        set_city('');
        set_statecd('');
        set_zip('');
        set_county('');
    };

    const onConfirmDelete = async () => {
        const keys = dataGrid.current?.instance?.getSelectedRowKeys() ?? [];
        if (keys.length > 0) {
            await hmdaApi.deleteData(keys);
            _afterRefresh();
        }
        set_openConfirmDelete(false);
    };

    const onCancelDelete = () => {
        set_openConfirmDelete(false);
    };

    const onConfirmRequeueApp = async () => {
        await requeueHMDAAsApp();
        set_openConfirmRequeueApp(false);
    };

    const onCancelRequeueApp = () => {
        set_openConfirmRequeueApp(false);
    };

    const requeueHMDAAsApp = async () => {
        try {
            //call api
            const response = await hmdaApi.requeueAllAsApp();
            if (response.statusCode !== 200) {
                return;
            }

            handleAdminSuccess('All HMDA Records Re-queued As Apps.');

            setTimeout(() => {
                set_openConfirmRequeueApp(false);
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const onConfirmRequeueLoan = async () => {
        await requeueHMDAAsLoans();
        set_openConfirmRequeueLoan(false);
    };

    const onCancelRequeueLoan = () => {
        set_openConfirmRequeueLoan(false);
    };

    const requeueHMDAAsLoans = async () => {
        try {
            //call api
            const response = await hmdaApi.requeueAllAsLoan();
            if (response.statusCode !== 200) {
                return;
            }

            handleAdminSuccess('All HMDA Records Re-queued As Loans.');

            setTimeout(() => {
                set_openConfirmRequeueLoan(false);
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const setRef = (ref) => {
        if (ref) {
            dataGrid.current = ref;
        }
    };

    const isFeatureEnabled = (featureId) => {
        if (!user || !user.featureList) {
            return false;
        }
        const feature = user.featureList.find((f) => f.featureId === featureId);
        return feature?.isEnabled ?? false;
    };

    const onToolbarPreparing = (e) => {
        e.toolbarOptions.items.push({
            widget: 'dxButton',
            options: {
                icon: 'trash',
                text: 'Delete',
                onClick() {
                    onDeleteClick();
                },
            },
            location: 'before',
        });

        e.toolbarOptions.items.push({
            widget: 'dxButton',
            options: {
                icon: 'revert',
                text: 'Clear Filters',
                onClick() {
                    onFilterResetClick();
                },
            },
            location: 'before',
        });

        e.toolbarOptions.items.push({
            widget: 'dxButton',
            options: {
                icon: 'export',
                text: 'Export',
                onClick() {
                    performExport();
                },
            },
            location: 'before',
        });

        e.toolbarOptions.items.push({
            widget: 'dxButton',
            options: {
                icon: 'deleterow',
                text: 'Manage Deleted',
                onClick() {
                    manageDeleted();
                },
            },
            location: 'before',
        });

        if (isFeatureEnabled(FeatureType.HMDARequeue)) {
            e.toolbarOptions.items.push({
                widget: 'dxButton',
                options: {
                    icon: 'pulldown',
                    text: 'Re-Queue Apps',
                    onClick() {
                        performRequeueApps();
                    },
                },
                location: 'before',
            });
            e.toolbarOptions.items.push({
                widget: 'dxButton',
                options: {
                    icon: 'pulldown',
                    text: 'Re-Queue Loans',
                    onClick() {
                        performRequeueLoans();
                    },
                },
                location: 'before',
            });
        }

        e.toolbarOptions.items.push({
            widget: 'dxCheckBox',
            options: {
                text: 'BFI Relevant Columns',
                value: bfiRelevantVisible,
                onValueChanged() {
                    set_bfiRelevantVisible(!bfiRelevantVisible);
                },
            },
            location: 'after',
        });

        e.toolbarOptions.items.push({
            widget: 'dxCheckBox',
            options: {
                text: 'Rows With Alerts',
                value: rowsWithAlertsVisible,
                onValueChanged() {
                    set_RowsWithAlertsVisible(!rowsWithAlertsVisible);
                },
            },
            location: 'after',
        });

        e.toolbarOptions.items.push({
            widget: 'dxCheckBox',
            options: {
                text: 'Non-Exportable',
                value: nonExportableVisible,
                onValueChanged() {
                    set_NonExportableVisible(!nonExportableVisible);
                },
            },
            location: 'after',
        });
    };

    const performExport = () => {
        const exportCriteria = {
            bfiNumber: bfiNumberRef.current,
            types: applicationTypesRef.current,
            applyStartDate: applyStartDateRef.current ? new Date(applyStartDateRef.current).toLocaleDateString('en-us') : null,
            applyEndDate: applyEndDateRef.current ? new Date(applyEndDateRef.current).toLocaleDateString('en-us') : null,
            actionTakenStartDate: actionTakenStartDateRef.current
                ? new Date(actionTakenStartDateRef.current).toLocaleDateString('en-us')
                : null,
            actionTakenEndDate: actionTakenEndDateRef.current
                ? new Date(actionTakenEndDateRef.current).toLocaleDateString('en-us')
                : null,
            uli: uliRef.current,
            street: streetRef.current,
            city: cityRef.current,
            state: stateRef.current,
            county: countyRef.current,
            zip: zipRef.current,
            showOnlyRowsWithAlerts: rowsWithAlertsVisibleRef.current,
        };

        dispatch(
            hmdaActions.setHmdaExportModal({
                hmdaExportModal: {
                    isOpen: true,
                    exportCriteria: exportCriteria,
                },
            })
        );
    };

    const manageDeleted = () => {
        dispatch(
            hmdaActions.setHmdaManageDeletedModal({
                hmdaManageDeletedModal: { isOpen: true },
            })
        );
    };

    const performRequeueApps = () => {
        set_openConfirmRequeueApp(true);
    };

    const performRequeueLoans = () => {
        set_openConfirmRequeueLoan(true);
    };

    return (
        <>
            <div id='data-grid-demo'>
                <div
                    className='scrollable-content-without-search'
                    style={{ padding: '1rem' }}
                >
                    <div
                        className='standard-form'
                        id='hmdaSearchFields'
                        style={{
                            border: '1px solid lightgrey',
                            borderRadius: '15px',
                            margin: '0 0 5px 0px',
                            backgroundColor: 'whitesmoke',
                        }}
                    >
                        <div
                            className='row'
                            style={{ margin: '10px 0 0 0' }}
                        >
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='bfiNumber'>BFI #</label>
                                <BfiNumber
                                    id={'bfiNumber'}
                                    onChange={set_bfiNumber}
                                    value={bfiNumber}
                                    required={false}
                                />
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='applicationTypes'>Application Types</label>
                                <TagBox
                                    id='applicationTypes'
                                    placeholder=''
                                    valueExpr='id'
                                    displayExpr='name'
                                    items={HMDALoanTypeOptions}
                                    value={applicationTypes}
                                    onValueChange={set_applicationTypes}
                                    showSelectionControls={true}
                                    applyValueMode='useButtons'
                                    multiline={true}
                                    height='35'
                                />
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='applyStartDate'>Apply Start Date</label>
                                <DateBox
                                    applyValueMode='useButtons'
                                    id='applyStartDate'
                                    min={new Date(2023, 0, 1)}
                                    value={applyStartDate ? new Date(applyStartDate) : null}
                                    onValueChange={set_applyStartDate}
                                    width={'auto'}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>Start Date of the Application Date Range</span>
                                </span>
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='applyEndDate'>Apply End Date</label>
                                <DateBox
                                    applyValueMode='useButtons'
                                    id='applyEndDate'
                                    value={applyEndDate ? new Date(applyEndDate) : null}
                                    max={new Date()}
                                    onValueChange={set_applyEndDate}
                                    width={'auto'}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>End Date of the Application Date Range</span>
                                </span>
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='actionTakenStartDate'>Action Taken Start Date</label>
                                <DateBox
                                    applyValueMode='useButtons'
                                    id='actionTakenStartDate'
                                    min={new Date(2023, 0, 1)}
                                    value={actionTakenStartDate ? new Date(actionTakenStartDate) : null}
                                    onValueChange={set_actionTakenStartDate}
                                    width={'auto'}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>Start Date of the Action Taken Date Range</span>
                                </span>
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='actionTakenEndDate'>Action Taken End Date</label>
                                <DateBox
                                    applyValueMode='useButtons'
                                    id='actionTakenEndDate'
                                    value={actionTakenEndDate ? new Date(actionTakenEndDate) : null}
                                    max={new Date()}
                                    onValueChange={set_actionTakenEndDate}
                                    width={'auto'}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>End Date of the Action Taken Date Range</span>
                                </span>
                            </div>
                        </div>
                        <div
                            className='row'
                            style={{ margin: '0 0 5px 0' }}
                        >
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='uli'>ULI</label>
                                <TextBox
                                    id='uli'
                                    value={uli}
                                    onValueChange={set_uli}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>
                                        Universal Loan Identifier number required to be generated to report HMDA data.
                                    </span>
                                </span>
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='street'>Street Address</label>
                                <TextBox
                                    id='street'
                                    value={street}
                                    onValueChange={set_street}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>Street number and name</span>
                                </span>
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='city'>City</label>
                                <TextBox
                                    id='city'
                                    value={city}
                                    onValueChange={set_city}
                                />
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='statecd'>State</label>
                                <StateSelect
                                    id='statecd'
                                    value={statecd}
                                    onChange={(event) => set_statecd(event.target.value)}
                                />
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='zip'>Zip</label>
                                <ZipCodeInput
                                    id='zipCode'
                                    value={zip}
                                    maxLength={5}
                                    onChange={(event) => set_zip(event.target.value)}
                                />
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>5 digit Zip Code.</span>
                                </span>
                            </div>
                            <div
                                className='columns small-12 large-2'
                                style={hmdaStyles.formColumn}
                            >
                                <label htmlFor='county'>County</label>
                                <CountySelect
                                    id={'county'}
                                    value={county ?? ''}
                                    selectedState={statecd ?? ''}
                                    onChange={(event) => set_county(event.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            textAlign: 'center',
                            textDecoration: 'italic',
                            color: '#2c5591',
                        }}
                    >
                        Double-Click on any cell to edit/view details
                    </div>
                    <DataGrid
                        id='hmdaGrid'
                        ref={(ref) => setRef(ref)}
                        dataSource={dataStore}
                        keyExpr='id'
                        showBorders={true}
                        showRowLines={false}
                        rowAlternationEnabled={true}
                        remoteOperations={true}
                        allowColumnResizing={true}
                        allowColumnReordering={false}
                        columnResizingMode={'nextColumn'}
                        cellHintEnabled={true}
                        columnAutoWidth={false}
                        customizeColumns={customizeColumns}
                        onCellPrepared={onCellPrepared}
                        onContentReady={onContentReady}
                        onCellDblClick={onCellDblClick}
                        onToolbarPreparing={onToolbarPreparing}
                    >
                        <Scrolling useNative={true} />
                        <LoadPanel enabled={true} />
                        {/* <StateStoring enabled={true} type="localStorage" storageKey="hmda" /> */}
                        <Selection mode='multiple' />
                        <Template
                            name='alphaNumTemplate'
                            render={alphaNumRender}
                        />
                        <Template
                            name='dateTemplate'
                            render={dateRender}
                        />
                        <Template
                            name='lookupTemplate'
                            render={lookupRender}
                        />
                        <Template
                            name='decimalTemplate'
                            render={decimalRender}
                        />
                        <Template
                            name='decimalSinglePrecisionTemplate'
                            render={decimalSinglePrecisionRender}
                        />
                        <Template
                            name='integerTemplate'
                            render={integerRender}
                        />
                        <Template
                            name='currencyTemplate'
                            render={currencyRender}
                        />
                        <Template
                            name='zipCodeTemplate'
                            render={zipCodeRender}
                        />
                        {/* <Editing mode="cell" allowUpdating={true} allowAdding={false} allowDeleting={false} /> */}
                        <Paging defaultPageSize={defaultPageSize} />
                        <Pager
                            displayMode='full'
                            showPageSizeSelector={allowedPageSizes.length > 1}
                            allowedPageSizes={allowedPageSizes}
                            showNavigationButtons={true}
                            showInfo={true}
                            visible={true}
                            location='before'
                        />
                    </DataGrid>
                </div>
            </div>
            <ConfirmModal
                isOpen={openConfirmDelete}
                text='Are you sure you want to delete the selected records?'
                confirmCallback={onConfirmDelete}
                cancelCallback={onCancelDelete}
            />
            <InformModal
                isOpen={informModalVisible}
                ititle=''
                text='No records selected!'
                confirmCallback={() => set_informModalVisible(false)}
            />
            <ConfirmModal
                isOpen={openConfirmRequeueApp}
                text='Are you sure you want to requeue all HMDA records as Application Updates?  If performing a full resync - ensure you also requeue as loans once application processing has completed in HMDA queue.  It may take some time to process - you will have to manually refresh the page periodically.'
                confirmCallback={onConfirmRequeueApp}
                cancelCallback={onCancelRequeueApp}
            />
            <ConfirmModal
                isOpen={openConfirmRequeueLoan}
                text='Are you sure you want to requeue all HMDA records as Loan Updates?  If performing a full resync - verify the application processing has completed in HMDA queue first.  It may take some time to process - you will have to manually refresh the page periodically.'
                confirmCallback={onConfirmRequeueLoan}
                cancelCallback={onCancelRequeueLoan}
            />
        </>
    );
};

export default HmdaSummary;
